/**
 * Data transfer object representing a My Site distributor.
 *
 * Note: the "mySite" property of this object is a doubly-nested map containing
 * this distributor's My Site stores, keyed first by subdomain name and then by country code.
 * Its structure is as follows:
 * <pre>
 *     {
 *       "some-store-name": {
 *         "US": MySiteData {...},
 *         "GB": MySiteData {...}
 *       },
 *       "other-store-name": {
 *         "CA": MySiteData {...}
 *       }
 *     }
 * </pre>
 * @property {String} displayName - Store name to display
 * @property {String} firstName - Distributor's first name to display
 * @property {String} lastName - Distributor's last name to display
 * @property {String} storeName - My Site store name
 * @property {String} email - Distributor's email address to display
 * @property {String} phone - Distributor's phone number to display
 * @property {String} photoURL - Url of distributor's photo to display // will be depreciated in V2
 * @property {String} profilePhoto - Url of distributor's profile photo
 * @property {String} siteLogo - Url of site logo photo
 * @property {Boolean} fullProductCatalog - Identifies whether my-site header has search bar
 * @property {Boolean} useProfilePhotoAsLogo - Identifies whether to use profile photo as a site logo
 * @property {Array<MySiteSocialAccount>} socialAccounts - List of distributor's social media accounts
 * @property {Object<String, Object<String, MySiteData>>} storefront - Nested map of distributor's My Site subdomains and the supported countries of each; keyed first by subdomain name and then by country code
 */
class MySiteSponsor {
    constructor(data = {}) {
        this.firstName = data.firstName;
        this.lastName = data.lastName;
        this.displayName = data.displayName;
        this.storeName = data.storeName;
        this.email = data.email;
        this.phone = data.phone;
        this.photoURL = data.photoURL;
        this.profilePhoto = data.profilePhoto;
        this.siteLogo = data.siteLogo;
        // this.fullProductCatalog = data.fullProductCatalog,
        this.useProfilePhotoAsLogo = data.useProfilePhotoAsLogo,
        this.socialAccounts = (data.socialAccounts || []).map(a => new MySiteSocialAccount(a));
        this.storefront = toMySiteCollection(data.storefront);
    }
}

/**
 * Data transfer object representing a My Site sponsor's social media account
 * @property {String} id - ID of the social media platform; used to determine the social icon to display
 * @property {String} name - Name of the social media platform
 * @property {String} url - Url of the distributor's account on the social media platform
 */
class MySiteSocialAccount {
    constructor(data = {}) {
        this.id = data.id;
        this.name = data.name;
        this.url = data.url;
    }
}

/**
 * Data transfer object representing a single My Site store
 * @property {boolean} showDefaultProducts - Flag indicating whether to show the market's default featured products
 * @property {boolean} showDefaultBundles - Flag indicating whether to show the market's default product bundle
 * @property {Array<String>} products - List of featured product SKUs; only used if not displaying the market's default featured products
 * @property {Array<String>} bundles - List of product SKUs in the product bundle; only used if not displaying the market's default product bundle
 * @property {boolean} published - Flag indicating whether this mySite has been published
 * @property {MySiteBanner} banner - My Site hero banner data
 * @property {Object<String, MySiteMessages>} lang - Map of mySite's localized messages; keyed by language code
 */
class MySiteData {
    constructor(data = {}) {
        this.bundles = data.bundles;
        this.products = data.products;
        this.published = data.published;
        this.lang = toMessagesCollection(data.lang);
        // TODO Remove this once we are ready for this to be referenced under the language in firebase
        this.showDefaultProducts = data.showDefaultProducts;
        this.showDefaultBundles = data.showDefaultBundles;
        this.banner = data.banner ? new MySiteBanner(data.banner) : undefined;
    }
}

/**
 * Data transfer object representing a collection of localized MySite messages
 * @property {String} checkoutMessage - Message to show customer upon checkout completion
 * @property {String} customBundlesMessage - Message to accompany the featured bundle
 * @property {String} welcomeMessage - Welcome message to show on MySite home page
 */
class MySiteMessages {
    constructor(data = {}) {       
        this.bundlesInfo = data.bundlesInfo || []
        this.banner = data.banner ? new MySiteBanner(data.banner) : undefined;
        this.storeName = data.storeName;
        this.aboutYourStore = data.aboutYourStore;
        this.aboutProducts = data.aboutProducts;
        this.purchaseMessage = data.purchaseMessage;
        this.lastEdited = data.lastEdited;
        this.default = data.default;
        this.fullProductCatalog = !!data.fullProductCatalog;
    }
}

/**
 * Data transfer object representing a MySite hero banner
 * @property {String} titleText - Banner title text
 * @property {String} bodyText - Banner body text
 * @property {String} ctaText - Call to Action text
 * @property {String} ctaLink - Call to Action link url
 * @property {{mobile: String, tablet: String, desktop: String}} imageUrls - Banner image urls for desktop, tablet, and mobile
 */
class MySiteBanner {
    constructor(data = {}) {
        this.titleText = data.titleText || '';
        this.bodyText = data.bodyText || '';
        this.ctaText = data.ctaText || '';
        this.ctaLink = data.ctaLink || '';
        this.backgroundColor = data.backgroundColor || '';
        this.scrimCopyColor = data.scrimCopyColor || '';
        this.imageUrls = Object.assign({}, data.imageUrls);
    }
}

/**
 * Data transfer object representing a market's featured product
 * @property {String} title - Localized title
 * @property {String} sku - Product SKU ID
 * @property {String} productPath - Relative path of this product on the universal product detail page
 * @property {String} path - relative path of product image
 * @property {String} baseSku - base SKU, if this product is a variant
 * @property {Array<String>} variants - list of variant SKUs, if any
 */
class FeaturedProduct {
    constructor(data = {}) {
        this.title = data.title;
        this.sku = data.sku;
        this.productPath = data.productPath;
        this.path = data.path;
        this.baseSku = data.baseSku;
        this.variants = data.variants;
    }
}

class MySiteBundle {
    constructor(data = {}) {
        this.id = data.id === undefined ? new Date().getTime() : data.id;
        this.skus = data.skus || [];
    }
}

export {MySiteSponsor, MySiteSocialAccount, MySiteData, MySiteMessages, MySiteBanner, FeaturedProduct, MySiteBundle};

/**
 * Private helper function to map JSON data to collection of {@link MySiteData} objects
 * @ignore
 * @param {*} data
 * @returns {Object<String, Object<String, MySiteData>>} collection of MySiteData, keyed first by subdomain and then by country code
 */
function toMySiteCollection(data = {}) {
    let collection = {};
    Object.keys(data).forEach(subdomain => {
        collection[subdomain] = {};
        Object.keys(data[subdomain]).forEach(cntry => {
            collection[subdomain][cntry] = new MySiteData(data[subdomain][cntry]);
        });
    });
    return collection;
}

/**
 * Private helper function to map JSON data to collection of {@link MySiteMessages} objects
 * @ignore
 * @param {*} data
 * @returns {Object<String, MySiteMessages>} collection of MySiteMessages, keyed by language code
 */
function toMessagesCollection(data = {}) {
    let collection = {};
    Object.keys(data).forEach(lang => collection[lang] = new MySiteMessages(data[lang]));
    return collection;
}
